import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';
import { Integration } from '@/components/integrations/HubSpotIntegration';

interface IntegrationsContextType {
  integrations: Integration[];
  isLoading: boolean;
  isTestingConnection: boolean;
  isSaving: boolean;
  toggleIntegration: (id: string) => void;
  updateCredential: (id: string, field: string, value: string) => void;
  testConnection: (id: string) => Promise<void>;
  saveSettings: (id: string) => Promise<void>;
}

const IntegrationsContext = createContext<IntegrationsContextType | undefined>(undefined);

export const useIntegrations = () => {
  const context = useContext(IntegrationsContext);
  if (!context) {
    throw new Error('useIntegrations must be used within an IntegrationsProvider');
  }
  return context;
};

interface IntegrationsProviderProps {
  children: ReactNode;
}

export const IntegrationsProvider: React.FC<IntegrationsProviderProps> = ({ children }) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load integrations on mount
  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        // In a real app, this would be an API call
        // For now, we'll use mock data
        setIntegrations([
          {
            id: 'hubspot',
            name: 'HubSpot',
            description: 'Sincronize clientes e assinaturas com o HubSpot CRM',
            logo: 'https://cdn.worldvectorlogo.com/logos/hubspot-1.svg',
            isActive: false,
            credentials: {
              apiKey: '',
            },
            status: 'disconnected',
          }
        ]);
      } catch (error) {
        console.error('Failed to load integrations:', error);
        toast.error('Falha ao carregar integrações');
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegrations();
  }, []);

  const toggleIntegration = (id: string) => {
    setIntegrations(prevIntegrations => 
      prevIntegrations.map(integration => 
        integration.id === id 
          ? { ...integration, isActive: !integration.isActive } 
          : integration
      )
    );
    
    const integration = integrations.find(i => i.id === id);
    if (integration) {
      toast.success(`Integração ${integration.isActive ? 'desativada' : 'ativada'} com sucesso`);
    }
  };

  const updateCredential = (id: string, field: string, value: string) => {
    setIntegrations(prevIntegrations => 
      prevIntegrations.map(integration => 
        integration.id === id 
          ? { 
              ...integration, 
              credentials: { 
                ...integration.credentials, 
                [field]: value 
              } 
            } 
          : integration
      )
    );
  };

  const testConnection = async (id: string) => {
    setIsTestingConnection(true);
    
    try {
      // In a real app, this would be an API call
      // For now, we'll simulate a delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const integration = integrations.find(i => i.id === id);
      if (integration && integration.credentials.apiKey) {
        setIntegrations(prevIntegrations => 
          prevIntegrations.map(integration => 
            integration.id === id 
              ? { ...integration, status: 'connected', lastSynced: new Date().toISOString() } 
              : integration
          )
        );
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error('Falha ao conectar. Verifique suas credenciais.');
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      toast.error('Falha ao testar conexão');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const saveSettings = async (id: string) => {
    setIsSaving(true);
    
    try {
      // In a real app, this would be an API call
      // For now, we'll simulate a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Falha ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  const value = {
    integrations,
    isLoading,
    isTestingConnection,
    isSaving,
    toggleIntegration,
    updateCredential,
    testConnection,
    saveSettings,
  };

  return (
    <IntegrationsContext.Provider value={value}>
      {children}
    </IntegrationsContext.Provider>
  );
};

export default IntegrationsContext; 